import {
  DefaultTransformer,
  deserialize,
  DocumentObject,
  JsonObject,
  serialize,
  transform,
  Transformer,
  whitelist,
} from '@jsonapiworld/jsonapi-serializer';
import { backendApiGateway } from '~/client/backend';
import { AccountResourceType } from '~/types/account';
import { CategoryResourceType, SubcategoryResourceType } from '~/types/category';
import { Company } from '~/types/company';
import { CostCenterResourceType } from '~/types/cost-center';
import { pagedMetaSchema } from '~/types/jsonapi';
import { TransactionRuleDirection } from '~/types/rule';
import {
  BulkTransactionUpdateAttributes,
  CashbookExportFormat,
  CATEGORIZATION_SOURCE_OTHER,
  CategorizationSources,
  DeduplicationOutcomeParameters,
  ShareCashbookParameters,
  Transaction,
  TransactionCreatePayload,
  TransactionFlowDirection,
  TransactionHiddenStatus,
  TransactionMeta,
  TransactionMetadata,
  TransactionMetadataFilter,
  TransactionProposedRules,
  TransactionResourceType,
  TransactionSorting,
  TransactionsPreviewFilter,
  TransactionStatus,
  TransactionUpdatePayload,
  VerificationStatus,
} from '~/types/transaction';
import { TransactionAllocationResourceType, TransactionAllocationUpdatePayload } from '~/types/transaction-allocation';
import { isDefined } from '~/types/typeGuards';
import { addCategoryFilter } from '~/utils/filters';
import {
  buildJsonAPIQueryParams,
  IJsonAPICursorPaginatorBuilder,
  IJsonAPIFilterBuilder,
  IJsonAPISortBuilder,
  jsonAPICursorPaginatorBuilder,
  jsonAPIFieldsBuilder,
  jsonAPIFilterBuilder,
  jsonAPIIncludeBuilder,
  jsonAPISortBuilder,
  PaginationInfo,
  SortInfo,
} from '~/utils/jsonapi-query';

type Filters = {
  companyId: Company['id'];
  excludeHiddenTransactions?: boolean;
  excludeFailedTransactions?: boolean;
  excludeUpcomingTransaction?: boolean;
} & TransactionMetadataFilter;

export const fetchTransaction = async (id: Transaction['id']): Promise<Transaction> => {
  const fieldsBuilder = createFieldsBuilder();
  const includeBuilder = createIncludeBuilder({ includeBookkeeping: true, includeDeduplicationTarget: true });
  const params = buildJsonAPIQueryParams(fieldsBuilder, includeBuilder);

  const { data } = await backendApiGateway.get<DocumentObject>(`/api/v1/transactions/${id}`, { params });

  return deserialize<Transaction>(data) as Transaction;
};

export const fetchSyncedDepositTransactions = async (
  companyId: Company['id'],
  depositTransactionId: Transaction['id'],
): Promise<Transaction[]> => {
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.eq('company.id', companyId);
  filterBuilder.eq('depositTransaction.id', depositTransactionId);
  const includeBuilder = jsonAPIIncludeBuilder(['account', 'account.consent.institution']);
  const params = buildJsonAPIQueryParams(filterBuilder, includeBuilder);

  const { data } = await backendApiGateway.get<DocumentObject>('/api/v1/transactions', { params });

  return (deserialize<Transaction>(data) ?? []) as Transaction[];
};

export const createTransaction = async (transaction: TransactionCreatePayload) => {
  const payload = serialize(transaction, TransactionResourceType, {
    relationships: ['account', 'card', 'category', 'subcategory', 'company'],
  });

  const { data } = await backendApiGateway.post<DocumentObject>('/api/v1/transactions', payload);

  return deserialize<Transaction>(data) as Transaction;
};

export const updateTransaction = async (transactionData: TransactionUpdatePayload) => {
  const payload = transform<TransactionUpdatePayload, {}>()
    .withInput(transactionData)
    .withTransformer(new TransactionUpdateTransformer())
    .serialize();

  const { data } = await backendApiGateway.patch<DocumentObject>(`/api/v1/transactions/${transactionData.id}`, payload);

  return deserialize<Transaction>(data) as Transaction;
};

export const deleteTransaction = async (id: Transaction['id']) => {
  await backendApiGateway.delete(`/api/v1/transactions/${id}`);
};

export const fetchTransactions = async (
  filters: Filters,
  sorting: SortInfo<TransactionSorting>,
  pagination: PaginationInfo,
  includeBookkeeping?: boolean,
): Promise<{
  data: Transaction[];
  meta: TransactionMeta;
}> => {
  const queryParams = transformFiltersToTransactionQuery(filters, sorting, pagination, includeBookkeeping);

  const { data } = await backendApiGateway.get<DocumentObject & { meta: TransactionMeta }>('/api/v1/transactions', {
    params: queryParams,
  });

  const transactions = deserialize<Transaction>(data) as Transaction[];
  return {
    data: transactions,
    meta: data.meta as TransactionMeta,
  };
};

export const bulkTransactionUpdate = async (attributes: BulkTransactionUpdateAttributes, filters: Filters) => {
  const params = transformFiltersToTransactionQuery(filters);
  const data = { attributes, companyId: filters.companyId };
  const headers = { 'content-type': 'application/json' };

  return backendApiGateway.post<{ num: number }>('/api/v1/transactions/bulk-update?', data, { params, headers });
};

export const fetchTransactionProposedRules = async (companyId: Company['id'], direction?: TransactionRuleDirection) => {
  const filtersBuilder = jsonAPIFilterBuilder();
  filtersBuilder.eq('company.id', companyId);

  if (direction === TransactionRuleDirection.Inflow) {
    filtersBuilder.gt('amountAmount', 0);
  } else if (direction === TransactionRuleDirection.Outflow) {
    filtersBuilder.lte('amountAmount', 0);
  }

  filtersBuilder.empty('account.hiddenAt', true);
  const queryParams = buildJsonAPIQueryParams(filtersBuilder);

  const { data: response } = await backendApiGateway.get<TransactionProposedRules>(
    '/api/v1/transactions/proposed-rules',
    { params: queryParams },
  );
  return response.data;
};

export const fetchTransactionReconciliationProposal = async (transactionIds: Transaction['id'][]) => {
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.in('id', transactionIds);
  filterBuilder.eq('verificationStatus', VerificationStatus.TO_VERIFY);

  const params = buildJsonAPIQueryParams(filterBuilder);

  const { data } = await backendApiGateway.get<
    {
      transaction_id: string;
      flow_ids: string[];
    }[]
  >('/api/v1/transactions/reconciliations', { params });

  return transactionIds.map((x) => {
    const flows = data.find((y) => y.transaction_id === x)?.flow_ids ?? [];
    return { transactionId: x, flowIds: flows };
  });
};

export const fetchTransactionMetadata = async (filters: Filters) => {
  const filterBuilder = createTransactionsFilterBuilder({ ...filters });
  const queryParams = buildJsonAPIQueryParams(filterBuilder);
  const { data } = await backendApiGateway.get<TransactionMetadata>('/api/v1/transactions/metadata', {
    params: queryParams,
  });

  return data;
};

export const fetchTransactionsPreview = async (
  companyId: string,
  filter: TransactionsPreviewFilter,
  pagination: PaginationInfo,
): Promise<{
  transactions: Transaction[];
  page: TransactionMeta['page'];
}> => {
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.eq('company.id', companyId);

  filterBuilder.like('search', filter.keywords);

  if (filter.flowDirection) {
    if (filter.flowDirection === TransactionFlowDirection.Inflow) filterBuilder.gt('amountAmount', 0);
    else filterBuilder.lte('amountAmount', 0);
  }
  if (filter.accountId) filterBuilder.eq('account.id', filter.accountId);
  else filterBuilder.empty('account.hiddenAt', true);

  if (filter.categoryStatus === 'uncategorized') filterBuilder.empty('category.id', true);

  if (filter.transactionTypes.length) {
    filterBuilder.in('transactionType', filter.transactionTypes);
  }

  const sortingBuilder = createSortingBuilder({ sort: 'date', direction: 'desc' });
  const includeBuilder = createIncludeBuilder();
  const paginationBuilder = createPaginationBuilder(pagination);

  const params = buildJsonAPIQueryParams(filterBuilder, sortingBuilder, includeBuilder, paginationBuilder);

  const { data } = await backendApiGateway.get<DocumentObject & { meta: TransactionMeta }>('/api/v1/transactions', {
    params,
  });

  const { page } = pagedMetaSchema.parse(data.meta);

  return {
    transactions: deserialize<Transaction>(data) as Transaction[],
    page,
  };
};

export const fetchTransactionsMetadataPreview = async (companyId: string, filter: TransactionsPreviewFilter) => {
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.eq('company.id', companyId);
  filterBuilder.like('search', filter.keywords);
  if (filter.flowDirection) {
    if (filter.flowDirection === TransactionFlowDirection.Inflow) filterBuilder.gt('amountAmount', 0);
    else filterBuilder.lte('amountAmount', 0);
  }
  if (filter.accountId) filterBuilder.eq('account.id', filter.accountId);
  else filterBuilder.empty('account.hiddenAt', true);

  if (filter.categoryStatus === 'uncategorized') filterBuilder.empty('category.id', true);

  if (filter.transactionTypes.length) {
    filterBuilder.in('transactionType', filter.transactionTypes);
  }

  const queryParams = buildJsonAPIQueryParams(filterBuilder);

  const { data } = await backendApiGateway.get<TransactionMetadata>('/api/v1/transactions/metadata', {
    params: queryParams,
  });

  return data;
};

export const exportTransactions = async (filters: Filters, sorting: SortInfo<TransactionSorting>) => {
  const queryParams = transformFiltersToTransactionQuery(filters, sorting);
  return await backendApiGateway.get<Blob>('/api/v1/transactions', {
    headers: {
      accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
    responseType: 'blob',
    params: queryParams,
  });
};

export const exportCashbook = async (
  filters: Filters,
  sorting: SortInfo<TransactionSorting>,
  format: CashbookExportFormat = 'standard',
) => {
  const queryParams = { ...transformFiltersToTransactionQuery(filters, sorting), format };
  return await backendApiGateway.get<Blob>('/api/v1/transactions/cashbook', {
    headers: {
      accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
    responseType: 'blob',
    params: queryParams,
  });
};

const transformFiltersToTransactionQuery = (
  filters: Filters,
  sorting?: SortInfo<TransactionSorting>,
  pagination?: PaginationInfo,
  includeBookkeeping?: boolean,
): ReturnType<typeof buildJsonAPIQueryParams> => {
  const filterBuilder = createTransactionsFilterBuilder({ ...filters });
  const sortingBuilder = createSortingBuilder(sorting);
  const includeBuilder = createIncludeBuilder({ includeBookkeeping });
  const paginationBuilder = createPaginationBuilder(pagination);
  const fieldsBuilder = createFieldsBuilder();

  return buildJsonAPIQueryParams(filterBuilder, sortingBuilder, includeBuilder, paginationBuilder, fieldsBuilder);
};

interface IncludeBuilderOptions {
  includeBookkeeping?: boolean;
  includeDeduplicationTarget?: boolean;
}

const createIncludeBuilder = ({
  includeBookkeeping = false,
  includeDeduplicationTarget = false,
}: IncludeBuilderOptions = {}) => {
  return jsonAPIIncludeBuilder([
    'account',
    ...(includeBookkeeping ? ['account.bookkeepingAccount', 'bookkeepingTransaction'] : []),
    'account.consent.institution',
    'allocations',
    'allocations.category',
    'allocations.subcategory',
    'allocations.costCenter',
    'attachments',
    'card',
    'category',
    'reconciliations',
    'subcategory',
    'payment',
    'payment.attachments',
    'depositTransaction',
    'depositTransaction.account.consent.institution',
    'deduplication',
    ...(includeDeduplicationTarget ? ['deduplication.targetTransaction'] : []),
  ]);
};

const createFieldsBuilder = () => {
  return jsonAPIFieldsBuilder([{ resource: 'reconciliation', fields: [] }]);
};

const createSortingBuilder = (sorting?: SortInfo<TransactionSorting>): IJsonAPISortBuilder => {
  return jsonAPISortBuilder([
    {
      sort: sorting?.sort ?? 'date',
      direction: sorting?.direction ?? 'desc',
    },
    {
      sort: 'createdAt',
      direction: sorting?.direction ?? 'desc',
    },
    {
      sort: 'id',
      direction: 'desc',
    },
  ]);
};

export const createTransactionsFilterBuilder = (filters: Filters): IJsonAPIFilterBuilder => {
  const filterBuilder = jsonAPIFilterBuilder();
  const {
    companyId,
    search,
    status,
    verificationStatus,
    date: [startDate, endDate],
    currency,
    amountRange: [fromAmount, toAmount, flowDirection],
    category,
    costCenter,
    categorizationSource,
    accountId,
    transactionType,
    cardHolder,
    hidden,
    selected,
    excluded,
    hasBookkeepingTransactions,
    excludeHiddenTransactions,
    excludeFailedTransactions,
    excludeUpcomingTransaction = true,
  } = filters;

  filterBuilder.eq('company.id', companyId);

  if (search) filterBuilder.ilike('search', search);
  if (status) filterBuilder.eq('status', status);
  if (verificationStatus) filterBuilder.eq('verificationStatus', verificationStatus);

  if (startDate) filterBuilder.gte('date', startDate);
  if (endDate) filterBuilder.lt('date', endDate);

  if (currency) filterBuilder.eq('amountCurrency', currency);

  if (isDefined(fromAmount) || isDefined(toAmount)) {
    filterBuilder.range('absAmountAmount', [fromAmount, toAmount]);
  }

  if (isDefined(flowDirection)) {
    filterBuilder.eq('isInflow', flowDirection === TransactionFlowDirection.Inflow);
  }

  addCategoryFilter(filterBuilder, category, 'has_category_id', 'has_subcategory_id');

  if (costCenter === null) {
    filterBuilder.empty('cost_centers_id', true);
  } else if (costCenter && costCenter.length >= 1) {
    filterBuilder.in('cost_centers_id', costCenter);
  }

  if (isDefined(categorizationSource)) {
    if (categorizationSource === CATEGORIZATION_SOURCE_OTHER) {
      filterBuilder.neq('categorizationSource', CategorizationSources.Automatic);
    } else {
      filterBuilder.eq('categorizationSource', categorizationSource);
    }
  }

  if (isDefined(accountId) && accountId.length > 0) {
    filterBuilder.in('account.id', accountId);
  } else {
    filterBuilder.empty('account.hiddenAt', true);
  }

  if (transactionType.length > 0) filterBuilder.in('transactionType', transactionType);

  if (cardHolder.length > 0) filterBuilder.in('card.id', cardHolder);

  if (excludeHiddenTransactions) filterBuilder.empty('hiddenAt', true);
  if (excludeFailedTransactions)
    filterBuilder.notIn('status', [TransactionStatus.Rejected, TransactionStatus.Canceled]);
  if (excludeUpcomingTransaction) filterBuilder.neq('status', TransactionHiddenStatus.Upcoming);

  if (hidden === true) {
    filterBuilder.empty('hiddenAt', false);
  } else if (hidden === false) {
    filterBuilder.empty('hiddenAt', true);
  }

  if (isDefined(hasBookkeepingTransactions)) {
    filterBuilder.empty('bookkeepingTransaction.id', !hasBookkeepingTransactions);
  }

  if (selected && selected.length > 0) filterBuilder.in('id', selected);
  if (excluded && excluded.length > 0) filterBuilder.notIn('id', excluded);

  return filterBuilder;
};

const createPaginationBuilder = (pagination?: PaginationInfo): IJsonAPICursorPaginatorBuilder => {
  const paginationBuilder = jsonAPICursorPaginatorBuilder();

  if (isDefined(pagination?.size)) paginationBuilder.size(pagination.size);
  if (isDefined(pagination?.cursor)) paginationBuilder.cursor(pagination.cursor);

  return paginationBuilder;
};

class TransactionUpdateTransformer extends Transformer<TransactionUpdatePayload, {}> {
  constructor() {
    super();
    this.type = TransactionResourceType;
    this.relationships = {
      account: this.buildRelationship('account', AccountResourceType),
      category: this.buildRelationship('category', CategoryResourceType),
      subcategory: this.buildRelationship('subcategory', SubcategoryResourceType),
      allocations: this.buildAllocationsRelationship(),
    };
  }

  transform(payload: TransactionUpdatePayload): JsonObject {
    const relationships = ['allocations'];
    const keys = Object.keys(payload).filter((val) => !relationships.includes(val));
    return whitelist(payload, keys);
  }

  buildRelationship(field: keyof TransactionUpdatePayload, resourceType: string, included = false) {
    return function (payload: Partial<TransactionUpdatePayload>) {
      return transform()
        .withInput(payload[field])
        .withTransformer(new DefaultTransformer(resourceType))
        .withIncluded(included)
        .toContext();
    };
  }

  buildAllocationsRelationship() {
    return function (payload: Partial<TransactionUpdatePayload>) {
      return transform<TransactionAllocationUpdatePayload, {}>()
        .withInput(payload.allocations as TransactionAllocationUpdatePayload[])
        .withTransformer(new TransactionAllocationTransformer())
        .withIncluded(true)
        .toContext();
    };
  }
}

class TransactionAllocationTransformer extends Transformer<TransactionAllocationUpdatePayload, {}> {
  constructor() {
    super();
    this.type = TransactionAllocationResourceType;
    this.relationships = {
      category: this.buildRelationship('category', CategoryResourceType),
      subcategory: this.buildRelationship('subcategory', SubcategoryResourceType),
      costCenter: this.buildRelationship('costCenter', CostCenterResourceType),
    };
  }

  transform(payload: TransactionAllocationUpdatePayload): JsonObject {
    return whitelist(payload, ['amount']);
  }

  buildRelationship(field: keyof TransactionAllocationUpdatePayload, resourceType: string) {
    return function (payload: Partial<TransactionAllocationUpdatePayload>) {
      return transform().withInput(payload[field]).withTransformer(new DefaultTransformer(resourceType)).toContext();
    };
  }
}

export const shareCashbook = async (params: ShareCashbookParameters): Promise<boolean> => {
  await backendApiGateway.post(`/api/v1/transactions/share-cashbook`, params, {
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return true;
};

export const markDeduplicationOutcome = async ({
  transactionId,
  originalTransactionId,
  outcome,
}: DeduplicationOutcomeParameters): Promise<boolean> => {
  await backendApiGateway.post(
    `/api/v1/transactions/${transactionId}/deduplication-outcome`,
    { originalTransactionId, outcome },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  return true;
};
